import React, { PureComponent } from "react";

import {
  CustomizedYAxisTick,
  CustomizedYAxisTickPercent,
  Error,
  selectFormat,
  StandardTooltip,
  formatDateFromData,
  isDateFromDataInISOFormat,
  CustomizedXAxisTickTimeDay,
  MAIN_COLORS,
  getAllKeys,
} from "../Utils/Utils.js";

import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

class LinesChart extends PureComponent {
  componentDidMount() {
    this.props.childRef(this);
  }

  getExportData = () => {
    return {
      data: this.props.data.data,
      opts: {
        type: "other",
        date: {
          defined: isDateFromDataInISOFormat(this.props.data.graph),
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: "YYYY-MM-DD",
        },
      },
    };
  };

  getQueryString = (url) => {
    const queryString = url.split("?")[1];
    if (!queryString) {
      console.error("Invalid URL format: no query string found");
      return "";
    }
    return queryString;
  };

  renderLegends = (data) => {
    const sortedKeys = this.getSortedKeys(data);
    return this.legendElement([...new Set(sortedKeys)]);
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);
    const keys = getAllKeys(data, ["date", "topContent", "total", "full_date"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  }

  legendElement = (data) => {
    return data.map((entry, i) => {
      return (
        <span key={i} className="legend mt-2 mt-md-0">
          <span
            className="legend__icon"
            style={{ background: MAIN_COLORS[i] }}
          ></span>
          <span className="legend__text" style={{ color: MAIN_COLORS[i] }}>
            {entry}
          </span>
        </span>
      );
    });
  };

  sortObjectByValues = (obj) => {
    const filteredEntries = Object.entries(obj).filter(
      ([key]) => key !== "date" && key !== "full_date"
    );

    const sortedEntries = filteredEntries.sort((a, b) => b[1] - a[1]);

    return sortedEntries.map(([v]) => v);
  };

  render() {
    const { data } = this.props.data;

    if (data.hasData === false) {
      return <Error minHeight={380} />;
    }

    let preparedData = formatDateFromData(this.props.data.data, "YYYY/MM/DD");

    const metrics = Object.keys(this.props.data.data[0]).filter(
      (key) => key !== "date" && key !== "full_date"
    );

    const yAxisAsPercentage = [
      "/lee/news-audience-board-by-day?t=last30&metric=returning-subs-pct",
      "/lee/news-audience-board-by-day?metric=returning-subs-pct",
      "/lee/news-audience-board-by-day?t=last90&metric=returning-users-pct",
      "/lee/news-audience-board-by-day?metric=returning-users-pct",
    ];

    const usePercentageAxis = yAxisAsPercentage.some((path) =>
      this.props.url.includes(path)
    );

    if (metrics.length === 0) {
      return <Error minHeight={380} />;
    }

    return (
      <>
        {usePercentageAxis && this.renderLegends(data)}
        <ResponsiveContainer height={380}>
          <LineChart
            data={preparedData}
            margin={{
              top: 20,
              right: 0,
              left: -45,
              bottom: 0,
            }}
          >
            <XAxis
              axisLine={false}
              tickLine={false}
              dataKey="date"
              tick={<CustomizedXAxisTickTimeDay />}
            />
            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={({ data }) => {
                    const payload = data(this.props);

                    if (payload !== null) {
                      const tooltip_data = payload[0].payload;

                      return (
                        <div
                          className="standard-tooltip"
                          style={{ width: "250px" }}
                        >
                          {tooltip_data && tooltip_data.date && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:
                              </span>
                              <span className="standard-tooltip__value-header">
                                {tooltip_data.date}
                              </span>
                            </header>
                          )}
                          <main
                            className="standard-tooltip__main"
                            style={{ width: "250px" }}
                          >
                            {tooltip_data &&
                              this.sortObjectByValues(payload[0].payload).map(
                                (metric, index) => (
                                  <div
                                    className="standard-tooltip__item"
                                    key={index}
                                  >
                                    <span className="standard-tooltip__key">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: payload[index]?.stroke,
                                        }}
                                      ></span>
                                      {metric}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {usePercentageAxis
                                        ? selectFormat(
                                            "percentage",
                                            tooltip_data[metric],
                                            "%0.0"
                                          )
                                        : selectFormat(
                                            "number",
                                            tooltip_data[metric],
                                            "0,00"
                                          )}
                                    </span>
                                  </div>
                                )
                              )}
                          </main>
                        </div>
                      );
                    }
                  }}
                />
              }
            />
            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />
            {metrics.map((metric, index) => (
              <Line
                key={index}
                type="monotone"
                dataKey={metric}
                stroke={MAIN_COLORS[index]}
                scale="auto"
                dot={false}
                isAnimationActive={false}
              />
            ))}

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={
                usePercentageAxis ? (
                  <CustomizedYAxisTickPercent />
                ) : (
                  <CustomizedYAxisTick />
                )
              }
            />
          </LineChart>
        </ResponsiveContainer>
      </>
    );
  }
}

export default LinesChart;
